import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { docsApi, Document } from '../../services/api';
import { useDocsStore } from '../../store/docsStore';
import styles from './QuickSearch.module.css';

interface QuickSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export const QuickSearch: React.FC<QuickSearchProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Document[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultListRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const navigate = useNavigate();
  
  const { isOnline, searchDocuments } = useDocsStore();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (resultListRef.current && itemRefs.current[selectedIndex]) {
      const selectedItem = itemRefs.current[selectedIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSelectedIndex(0);
      return;
    }

    if (!isOnline) {
      const localResults = searchDocuments(query);
      setResults(localResults);
      setSelectedIndex(0);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await docsApi.getAll({ search: query });
        setResults(response.data);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search failed:', error);
        const localResults = searchDocuments(query);
        setResults(localResults);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [query, isOnline, searchDocuments]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[selectedIndex]) {
        openDocument(results[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const openDocument = (doc: Document) => {
    navigate(`/docs/${doc.slug}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.searchBox}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search documents..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.results}>
          {loading && <div className={styles.loading}>Searching...</div>}
          
          {!loading && query && results.length === 0 && (
            <div className={styles.noResults}>No documents found</div>
          )}

          {!loading && results.length > 0 && (
            <div className={styles.resultList} ref={resultListRef}>
              {results.map((doc, index) => (
                <div
                  key={doc.id}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className={`${styles.resultItem} ${index === selectedIndex ? styles.selected : ''}`}
                  onClick={() => openDocument(doc)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className={styles.resultTitle}>{doc.title}</div>
                  <div className={styles.resultPath}>{doc.folder_path}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.hint}>
            <kbd>↑↓</kbd> Navigate · <kbd>Enter</kbd> Open · <kbd>Esc</kbd> Close
          </div>
        </div>
      </div>
    </div>
  );
};
